
import React, { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";

import { useConfig } from "../../context/ConfigContext.js";
import "../Styles/RafflePrizes.css";

const EMPTY_PRIZE = {
  name: "",
  description: "",
  donatedBy: "",
  imageUrl: "",
  visible: true,
  order: 0,
};

export default function RafflePrizesTab() {
  const { config } = useConfig();

  const apiBase = (config?.apiUrl || "/api").replace(/\/$/, "");
  const endpoint = `${apiBase}/admin/raffle/prizes`;

  const [prizes, setPrizes] = useState([]);

  const [newPrize, setNewPrize] = useState({ ...EMPTY_PRIZE });
  const [addingPrize, setAddingPrize] = useState(false);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [mode, setMode] = useState("append");

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PRIZE });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // ==========================================
  // LOAD PRIZES
  // ==========================================

  const loadPrizes = useCallback(async () => {
    try {
      setLoading(true);

      const res = await fetch(endpoint, {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Unable to load prizes.");
      }

      setPrizes(data.prizes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    loadPrizes();
  }, [loadPrizes]);

  // ==========================================
  // ADD INDIVIDUAL PRIZE
  // ==========================================

  const handleAddPrize = async (e) => {
    e.preventDefault();

    if (!newPrize.name.trim()) {
      setError("Please enter a prize name.");
      return;
    }

    try {
      setAddingPrize(true);
      setError("");
      setMessage("");

      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          name: newPrize.name.trim(),
          description: newPrize.description.trim(),
          donatedBy: newPrize.donatedBy.trim(),
          imageUrl: newPrize.imageUrl.trim(),
          visible: newPrize.visible,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.message || "Unable to add raffle prize."
        );
      }

      setNewPrize({ ...EMPTY_PRIZE });

      await loadPrizes();

      setMessage("Prize added successfully!");
    } catch (err) {
      console.error("Add raffle prize error:", err);
      setError(err.message);
    } finally {
      setAddingPrize(false);
    }
  };

  // ==========================================
  // SPREADSHEET PREVIEW
  // ==========================================

  const handleFile = async (selectedFile) => {
    setFile(selectedFile || null);
    setPreview([]);
    setError("");
    setMessage("");

    if (!selectedFile) return;

    try {
      if (!/\.(xlsx|csv)$/i.test(selectedFile.name)) {
        throw new Error("Please select an XLSX or CSV file.");
      }

      if (selectedFile.size > 2 * 1024 * 1024) {
        throw new Error("Maximum file size is 2 MB.");
      }

      const buffer = await selectedFile.arrayBuffer();

      const workbook = XLSX.read(buffer, {
        type: "array",
      });

      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      if (!sheet) {
        throw new Error("The spreadsheet is empty.");
      }

      const rows = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
      });

      if (!rows.length) {
        throw new Error("No prize rows were found.");
      }

      setPreview(rows);
    } catch (err) {
      setError(err.message);
      setFile(null);
    }
  };

  // ==========================================
  // IMPORT SPREADSHEET
  // ==========================================

  const handleImport = async () => {
    if (!file || !preview.length) return;

    if (
      mode === "replace" &&
      !window.confirm(
        "This will permanently replace ALL existing raffle prizes. Continue?"
      )
    ) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const body = new FormData();

      body.append("file", file);
      body.append("mode", mode);

      const res = await fetch(`${endpoint}/import`, {
        method: "POST",
        credentials: "include",
        body,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Import failed.");
      }

      setFile(null);
      setPreview([]);

      await loadPrizes();

      setMessage(data.message || "Prizes imported successfully.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // EDIT PRIZE
  // ==========================================

  const startEditing = (prize) => {
    setEditing(prize._id);

    setForm({
      name: prize.name || "",
      description: prize.description || "",
      donatedBy: prize.donatedBy || "",
      imageUrl: prize.imageUrl || "",
      visible: prize.visible,
      order: prize.order ?? 0,
    });

    setError("");
    setMessage("");
  };

  const savePrize = async (e) => {
    e.preventDefault();

    if (!editing) return;

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const res = await fetch(`${endpoint}/${editing}`, {
        method: "PATCH",

        headers: {
          "Content-Type": "application/json",
        },

        credentials: "include",
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.message || "Unable to save prize."
        );
      }

      setEditing(null);
      setForm({ ...EMPTY_PRIZE });

      await loadPrizes();

      setMessage("Prize updated successfully.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // DELETE PRIZE
  // ==========================================

  const deletePrize = async (prize) => {
    if (
      !window.confirm(
        `Delete "${prize.name}" permanently?`
      )
    ) {
      return;
    }

    try {
      setError("");
      setMessage("");

      const res = await fetch(
        `${endpoint}/${prize._id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.message || "Unable to delete prize."
        );
      }

      if (editing === prize._id) {
        setEditing(null);
      }

      await loadPrizes();

      setMessage("Prize deleted.");
    } catch (err) {
      setError(err.message);
    }
  };

  // ==========================================
  // UPDATE VISIBILITY / OTHER FIELDS
  // ==========================================

  const updatePrize = async (prize, changes) => {
    try {
      setError("");
      setMessage("");

      const res = await fetch(
        `${endpoint}/${prize._id}`,
        {
          method: "PATCH",

          headers: {
            "Content-Type": "application/json",
          },

          credentials: "include",

          body: JSON.stringify({
            name: prize.name,
            description: prize.description,
            donatedBy: prize.donatedBy,
            imageUrl: prize.imageUrl,
            visible: prize.visible,
            order: prize.order,
            ...changes,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.message || "Unable to update prize."
        );
      }

      await loadPrizes();
    } catch (err) {
      setError(err.message);
    }
  };

  // ==========================================
  // MOVE PRIZE UP / DOWN
  // ==========================================

  const movePrize = async (prize, direction) => {
    const sorted = [...prizes].sort(
      (a, b) => a.order - b.order
    );

    const index = sorted.findIndex(
      (item) => item._id === prize._id
    );

    const other = sorted[index + direction];

    if (!other) return;

    try {
      setError("");

      const update = async (item, order) => {
        const res = await fetch(
          `${endpoint}/${item._id}`,
          {
            method: "PATCH",

            headers: {
              "Content-Type": "application/json",
            },

            credentials: "include",

            body: JSON.stringify({
              name: item.name,
              description: item.description,
              donatedBy: item.donatedBy,
              imageUrl: item.imageUrl,
              visible: item.visible,
              order,
            }),
          }
        );

        if (!res.ok) {
          throw new Error("Unable to reorder prizes.");
        }
      };

      await update(prize, index + direction);
      await update(other, index);

      await loadPrizes();
    } catch (err) {
      setError(err.message);
    }
  };

  // ==========================================
  // PAGE
  // ==========================================

  return (
    <div className="raffle-prizes-admin">
      <h2>Raffle Prizes</h2>

      <p>
        Add prizes individually, import a spreadsheet,
        or manage the prizes displayed in the public
        raffle widget.
      </p>

      {message && (
        <p className="raffle-admin-success">
          {message}
        </p>
      )}

      {error && (
        <p className="raffle-admin-error">
          {error}
        </p>
      )}

      {/* ======================================
          ADD INDIVIDUAL PRIZE
      ====================================== */}

      <section className="raffle-admin-card raffle-add-prize-card">
        <h3>Add Individual Prize</h3>

        <p>
          Add a prize manually without uploading
          a spreadsheet.
        </p>

        <form
          className="raffle-edit-form"
          onSubmit={handleAddPrize}
        >
          <label>
            Prize Name *

            <input
              type="text"
              placeholder="e.g. £50 Restaurant Voucher"
              maxLength={200}
              value={newPrize.name}
              required
              onChange={(e) =>
                setNewPrize({
                  ...newPrize,
                  name: e.target.value,
                })
              }
            />
          </label>

          <label>
            Description

            <textarea
              placeholder="Enter a description of the prize..."
              maxLength={2000}
              value={newPrize.description}
              onChange={(e) =>
                setNewPrize({
                  ...newPrize,
                  description: e.target.value,
                })
              }
            />
          </label>

          <label>
            Donated By

            <input
              type="text"
              placeholder="e.g. Local Restaurant"
              maxLength={200}
              value={newPrize.donatedBy}
              onChange={(e) =>
                setNewPrize({
                  ...newPrize,
                  donatedBy: e.target.value,
                })
              }
            />
          </label>

          <label>
            Image URL (Optional)

            <input
              type="url"
              placeholder="https://example.com/prize.jpg"
              value={newPrize.imageUrl}
              onChange={(e) =>
                setNewPrize({
                  ...newPrize,
                  imageUrl: e.target.value,
                })
              }
            />
          </label>

          {newPrize.imageUrl && (
            <div className="raffle-add-image-preview">
              <img
                key={newPrize.imageUrl}
                src={newPrize.imageUrl}
                alt="Prize preview"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          )}

          <label className="raffle-add-visibility">
            <input
              type="checkbox"
              checked={newPrize.visible}
              onChange={(e) =>
                setNewPrize({
                  ...newPrize,
                  visible: e.target.checked,
                })
              }
            />

            Display this prize on the public website
          </label>

          <div className="raffle-add-buttons">
            <button
              type="submit"
              className="btn primary"
              disabled={addingPrize}
            >
              {addingPrize
                ? "Adding Prize..."
                : "+ Add Prize"}
            </button>

            <button
              type="button"
              className="btn outline"
              disabled={addingPrize}
              onClick={() => {
                setNewPrize({ ...EMPTY_PRIZE });
                setError("");
              }}
            >
              Clear Form
            </button>
          </div>
        </form>
      </section>

      {/* ======================================
          IMPORT SPREADSHEET
      ====================================== */}

      <section className="raffle-admin-card">
        <h3>Import Spreadsheet</h3>

        <p>
          Supported formats: Excel (.xlsx) and CSV (.csv).
        </p>

        <input
          type="file"
          accept=".xlsx,.csv"
          onChange={(e) =>
            handleFile(e.target.files?.[0])
          }
        />

        <div className="raffle-import-options">
          <label>
            <input
              type="radio"
              name="importMode"
              value="append"
              checked={mode === "append"}
              onChange={() => setMode("append")}
            />

            Add to existing prizes
          </label>

          <label>
            <input
              type="radio"
              name="importMode"
              value="replace"
              checked={mode === "replace"}
              onChange={() => setMode("replace")}
            />

            Replace all existing prizes
          </label>
        </div>

        {preview.length > 0 && (
          <>
            <h4>
              Spreadsheet Preview ({preview.length} prizes)
            </h4>

            <div className="raffle-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Prize</th>
                    <th>Description</th>
                    <th>Donated By</th>
                    <th>Display</th>
                  </tr>
                </thead>

                <tbody>
                  {preview.map((row, index) => (
                    <tr key={index}>
                      <td>
                        {row["Prize Name"] ?? row.name}
                      </td>

                      <td>
                        {row.Description ?? row.description}
                      </td>

                      <td>
                        {row["Donated By"] ?? row.donatedBy}
                      </td>

                      <td>
                        {String(
                          row.Display ?? row.visible ?? "Yes"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="btn primary"
              disabled={saving}
              onClick={handleImport}
            >
              {saving
                ? "Importing..."
                : `Import ${preview.length} Prizes`}
            </button>
          </>
        )}
      </section>

      {/* ======================================
          CURRENT PRIZES
      ====================================== */}

      <section className="raffle-admin-card">
        <h3>Current Prizes ({prizes.length})</h3>

        {loading && <p>Loading prizes...</p>}

        <div className="raffle-admin-list">
          {prizes.map((prize) => (
            <div
              className="raffle-admin-item"
              key={prize._id}
            >
              {prize.imageUrl && (
                <img
                  src={prize.imageUrl}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}

              <div className="raffle-admin-item-info">
                <strong>{prize.name}</strong>

                <small>
                  {prize.donatedBy || "No donor specified"}
                </small>

                <span>
                  {prize.visible ? "Visible" : "Hidden"}
                </span>
              </div>

              <div className="raffle-admin-actions">
                <button
                  type="button"
                  onClick={() => movePrize(prize, -1)}
                  title="Move up"
                >
                  ↑
                </button>

                <button
                  type="button"
                  onClick={() => movePrize(prize, 1)}
                  title="Move down"
                >
                  ↓
                </button>

                <button
                  type="button"
                  onClick={() =>
                    updatePrize(prize, {
                      visible: !prize.visible,
                    })
                  }
                >
                  {prize.visible ? "Hide" : "Show"}
                </button>

                <button
                  type="button"
                  onClick={() => startEditing(prize)}
                >
                  Edit
                </button>

                <button
                  type="button"
                  className="danger"
                  onClick={() => deletePrize(prize)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ======================================
          EDIT EXISTING PRIZE
      ====================================== */}

      {editing && (
        <section className="raffle-admin-card">
          <h3>Edit Prize</h3>

          <form
            className="raffle-edit-form"
            onSubmit={savePrize}
          >
            <label>
              Prize Name

              <input
                value={form.name}
                required
                maxLength={200}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                  })
                }
              />
            </label>

            <label>
              Description

              <textarea
                value={form.description}
                maxLength={2000}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: e.target.value,
                  })
                }
              />
            </label>

            <label>
              Donated By

              <input
                value={form.donatedBy}
                maxLength={200}
                onChange={(e) =>
                  setForm({
                    ...form,
                    donatedBy: e.target.value,
                  })
                }
              />
            </label>

            <label>
              Image URL

              <input
                type="url"
                value={form.imageUrl}
                onChange={(e) =>
                  setForm({
                    ...form,
                    imageUrl: e.target.value,
                  })
                }
              />
            </label>

            <label>
              Display Order

              <input
                type="number"
                value={form.order}
                onChange={(e) =>
                  setForm({
                    ...form,
                    order: Number(e.target.value),
                  })
                }
              />
            </label>

            <label>
              <input
                type="checkbox"
                checked={form.visible}
                onChange={(e) =>
                  setForm({
                    ...form,
                    visible: e.target.checked,
                  })
                }
              />

              Visible on website
            </label>

            <div>
              <button
                type="submit"
                className="btn primary"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>

              <button
                type="button"
                className="btn outline"
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
